/**
 * Created: 2026-09-25
 * Initial commit: Move booking slot merging logic to shared
 *
 * Utilities for merging consecutive booking slots into time ranges.
 * Shared between mini and web frontends.
 */

export interface BookingSlot {
  courtId: string
  courtName?: string
  startTime: string
  price: number
  cancelledAt?: string | null
}

export interface MergedSlot {
  courtId: string
  courtName?: string
  date: string
  startHour: number
  endHour: number
  price: number
  cancelledAt?: string | null
}

/**
 * Merge consecutive booking slots for the same court into time ranges.
 * Slots are consecutive if they share the same courtId, same date, adjacent hours, and both are active (not cancelled).
 * Cancelled slots (where cancelledAt is not null) are never merged and stay as individual slots.
 */
export function mergeConsecutiveSlots(slots: BookingSlot[]): MergedSlot[] {
  if (slots.length === 0) return []

  const toMergeKey = (s: BookingSlot) => {
    const d = new Date(s.startTime)
    const date = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`
    return { courtId: s.courtId, courtName: s.courtName, date, hour: d.getHours(), cancelledAt: s.cancelledAt }
  }

  const sorted = [...slots].sort((a, b) => {
    const ka = toMergeKey(a)
    const kb = toMergeKey(b)
    if (ka.courtId !== kb.courtId) return ka.courtId.localeCompare(kb.courtId)
    if (ka.date !== kb.date) return ka.date.localeCompare(kb.date)
    return ka.hour - kb.hour
  })
  const first = toMergeKey(sorted[0])
  const merged: MergedSlot[] = []
  let current: MergedSlot = {
    courtId: first.courtId,
    courtName: first.courtName,
    date: first.date,
    startHour: first.hour,
    endHour: first.hour + 1,
    price: Number(sorted[0].price),
    cancelledAt: first.cancelledAt,
  }

  for (let i = 1; i < sorted.length; i++) {
    const key = toMergeKey(sorted[i])
    const isCancelled = key.cancelledAt != null
    const isCurrentCancelled = current.cancelledAt != null

    // Never merge cancelled slots or merge into/from cancelled slots
    const canMerge =
      !isCancelled &&
      !isCurrentCancelled &&
      key.courtId === current.courtId &&
      key.date === current.date &&
      key.hour === current.endHour &&
      Number(sorted[i].price) === current.price / (current.endHour - current.startHour)

    if (canMerge) {
      current.endHour = key.hour + 1
      current.price += Number(sorted[i].price)
    } else {
      merged.push(current)
      current = {
        courtId: key.courtId,
        courtName: key.courtName,
        date: key.date,
        startHour: key.hour,
        endHour: key.hour + 1,
        price: Number(sorted[i].price),
        cancelledAt: key.cancelledAt,
      }
    }
  }

  merged.push(current)
  return merged
}
